import { formatDateString } from '@frontend/date';
import type { AutofillData } from './autofill.types';

// Allowed field types for pre-populate elements (strict allow-list per spec §4.5)
const ALLOWED_PREPOPULATE_TYPES = new Set([
  'date',
  'text',
  'textarea',
  'email',
  'phone',
  'postalCode',
  'radio',
  'checklist',
]);

const mapPerson = (person: NonNullable<AutofillData['personDetails']>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  if (person.firstName) result['firstName'] = person.firstName;
  if (person.lastName) result['lastName'] = person.lastName;
  if (person.middleName) result['middleName'] = person.middleName;
  if (person.email) result['email'] = person.email;
  if (person.phoneNumber) result['mobilePhone'] = person.phoneNumber;
  if (person.workPhone) result['workPhone'] = person.workPhone;
  if (person.homePhone) result['homePhone'] = person.homePhone;
  if (person.maritalStatus) result['maritalStatus'] = person.maritalStatus;
  if (person.preferredName) result['preferredName'] = person.preferredName;
  if (person.driverLicense) result['driversLicenseNumber'] = person.driverLicense;
  if (person.streetAddress1) result['address1'] = person.streetAddress1;
  if (person.city) result['city'] = person.city;
  if (person.state) result['state'] = person.state;
  if (person.zipCode) result['postalCode'] = person.zipCode;

  const birthdate = person.birthdate ? formatDateString(person.birthdate, 'MM/DD/YYYY') : undefined;
  if (birthdate) result['birthdate'] = birthdate;

  // gender must be lowercase to match form option values
  if (person.gender) result['gender'] = person.gender.toLowerCase();

  // socialSecurityNumber is intentionally excluded — never autofill SSN

  return result;
};

const mapMedicalHistory = (medicalHistory: NonNullable<AutofillData['medicalHistory']>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  if (medicalHistory.allergies?.length) result['allergies'] = medicalHistory.allergies;
  if (medicalHistory.medications?.length) result['medications'] = medicalHistory.medications;
  if (medicalHistory.disease?.length) result['disease'] = medicalHistory.disease;

  return result;
};

const mapInsurance = (
  insurance: NonNullable<AutofillData['primaryInsurance']>,
  prefix: 'primary' | 'secondary'
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  const p = prefix;

  if (insurance.companyName) result[`${p}InsuranceName`] = insurance.companyName;
  if (insurance.id) result[`${p}InsuranceId`] = insurance.id;
  if (insurance.groupNumber) result[`${p}GroupNumber`] = insurance.groupNumber;
  if (insurance.patientRelationship) result[`${p}InsuranceRelationship`] = insurance.patientRelationship;
  if (insurance.policyHolderName) result[`${p}PolicyHolderName`] = insurance.policyHolderName;
  if (insurance.rxNumber) result[`${p}RxNumber`] = insurance.rxNumber;
  if (insurance.spouseName) result[`${p}InsuranceSpouseName`] = insurance.spouseName;

  // Flatten nested address fields
  const addr = insurance.address;
  if (addr) {
    if (addr.city) result[`${p}InsuranceCity`] = addr.city;
    if (addr.state) result[`${p}InsuranceState`] = addr.state;
    if (addr.zipCode) result[`${p}InsurancePostalCode`] = addr.zipCode;
    if (addr.streetAddress1) result[`${p}InsuranceAddress1`] = addr.streetAddress1;
    if (addr.streetAddress2) result[`${p}InsuranceAddress2`] = addr.streetAddress2;
  }

  // ssn and spouseSsn are intentionally excluded — never autofill insurance SSN

  return result;
};

const mapPrePopulateElements = (
  elements: NonNullable<AutofillData['prePopulateElements']>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [fieldId, element] of Object.entries(elements)) {
    const { type, value } = element;
    if (!type || !ALLOWED_PREPOPULATE_TYPES.has(type) || value == null) continue;

    if (type === 'date') {
      const reformatted = formatDateString(value, 'MM/DD/YYYY');
      if (reformatted) result[fieldId] = reformatted;
    } else if (type === 'checklist') {
      try {
        result[fieldId] = JSON.parse(value);
      } catch {
        // skip malformed JSON
      }
    } else {
      result[fieldId] = value;
    }
  }

  return result;
};

/**
 * Merges all autofill data sources into a single flat map of virtualFieldId → value.
 * Pre-populate elements are applied last and win over same-key values from other sources.
 */
export const buildAutofillValues = (data: Partial<AutofillData>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  if (data.personDetails) Object.assign(result, mapPerson(data.personDetails));
  if (data.medicalHistory) Object.assign(result, mapMedicalHistory(data.medicalHistory));
  if (data.primaryInsurance) Object.assign(result, mapInsurance(data.primaryInsurance, 'primary'));
  if (data.secondaryInsurance) Object.assign(result, mapInsurance(data.secondaryInsurance, 'secondary'));
  // pre-populate elements applied last — highest priority, may override any of the above
  if (data.prePopulateElements) Object.assign(result, mapPrePopulateElements(data.prePopulateElements));

  return result;
};
